import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {
  ActivatedRoute,
  NavigationEnd,
  ParamMap,
  Router
} from '@angular/router';
import { filter, finalize, map, of, Subscription, switchMap } from 'rxjs';
import { FilterBuilderComponent } from 'src/app/components/system/filter-builder/filter-builder.component';
import {
  ItemSearchOptionField,
  ItemSearchOptionFieldOption
} from 'src/app/components/filter-list/models/filterClasses';
import { TableColumn } from 'src/app/components/common/common-table/common-table.component';
import {
  FilterBuilderFilter,
  FilterBuilderOutput,
  FilterBuilderParam,
  FilterBuilderParamOpts
} from 'src/app/models/filter-builder';
import { Client } from 'src/app/models/client';
import { WorkflowContextService, WorkflowService } from 'src/app/services';
import { MyRegistrationsActionCellComponent } from './my-registrations-action-cell/my-registrations-action-cell.component';
import { MyRegistrationsStatusPillComponent } from './my-registrations-status-pill/my-registrations-status-pill.component';

type RegistrationStatus = 'Expired' | 'Expiring Soon' | 'Inactive';

interface RegistrationRow {
  id: string;
  applicationId?: string;
  registrationInfo: {
    name: string;
  };
  registrationInfoText: string;
  type: string;
  registeredOn: string;
  effectiveOn: string;
  expiresOn: string;
  status: RegistrationStatus;
  renewLabel: string;
}

interface SortOption {
  label: string;
  sortField: string;
  sortDescending: boolean;
}

interface MyRegistrationsRouteState {
  page: number;
  searchQuery: string;
  activeFilters: FilterBuilderOutput;
  sortField: string;
  sortDescending: boolean;
}

@Component({
  selector: 'app-my-registrations',
  standalone: false,
  templateUrl: './my-registrations.component.html',
  styleUrls: ['./my-registrations.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class MyRegistrationsComponent
  implements OnInit, AfterViewInit, OnDestroy {
  private readonly subscriptions = new Subscription();
  private readonly FILTER_CACHE_KEY = 'my-registrations-state';
  private readonly PAGE_QUERY_PARAM = 'p';
  private readonly SEARCH_QUERY_PARAM = 'q';
  private readonly FILTER_QUERY_PARAM = 'af';
  private readonly SORT_FIELD_QUERY_PARAM = 'sf';
  private readonly SORT_DESC_QUERY_PARAM = 'sd';
  private pendingRouteState: MyRegistrationsRouteState | null = null;
  private pendingQueryParams: ParamMap | null = null;
  private lastRouteStateSignature = '';
  // Added by Amisha on 06/17/2026.
  // Reason: applicant renewals route/query sync can re-enter during startup, so cache the last
  // initialized jurisdiction and in-flight requests to prevent duplicate filter and table APIs.
  private initializedFiltersClientId: string | null = null;
  private initFiltersPromise: Promise<void> | null = null;
  private activeFetchRequestKey: string | null = null;

  clientId = '';
  filterCriteria: FilterBuilderParam[] = [];
  workflowOpts: FilterBuilderParamOpts[] = [];
  columns: TableColumn[] = [];
  displayedColumns: string[] = [];
  data: RegistrationRow[] = [];
  pageSize = 10;
  currentPage = 0;
  totalPages = 0;
  totalRecords = 0;
  isLoading = true;
  sortField = 'registeredOn';
  sortDescending = true;
  isSortOpen = false;
  selectedSortLabel = 'All';
  activeFilters: FilterBuilderOutput = {};
  searchQuery = '';
  defaultFilters: FilterBuilderFilter[] = [];
  filtersReady = false;
  showOverlay = false;
  isOverlayOpen = false;
  isMobileDevice = false;
  isRenewNavigationLoading = false;
  renewingRegistrationId: string | null = null;
  private hadOverlayChildRoute = false;
  private isSyncingFilterBuilderState = false;
  private filterBuilderComponent?: FilterBuilderComponent;
  @ViewChild(FilterBuilderComponent)
  
  set filterBuilder(value: FilterBuilderComponent | undefined) {
    this.filterBuilderComponent = value;
    this.syncFilterBuilderState();
  }
      @ViewChild('sortWrapper') sortWrapper!: ElementRef;

  filterTypes = [
    {
      name: 'contains'
    },
    {
      name: 'is'
    },
    {
      name: 'in'
    },
    {
      name: 'range'
    }
  ];
  private readonly PAGE_SIZE_STORAGE_KEY =
    'wm-common-table-page-size-renewals-applicant-janus';

  private readonly simpleSearchCriteria: Array<{
    id: string;
    inputType?: string;
  }> = [
    { id: 'appDescription' },
    { id: 'registeredOn', inputType: 'date' },
    { id: 'effectiveOn', inputType: 'date' },
    { id: 'expiresOn', inputType: 'date' },
    { id: 'approvedOn', inputType: 'date' },
    { id: 'rejectedOn', inputType: 'date' },
    { id: 'notes' },
    { id: 'renewalType' }
  ];

  readonly sortOptions: SortOption[] = [
    { label: 'All', sortField: 'registeredOn', sortDescending: true },
    {
      label: 'Registration Info',
      sortField: 'appDescription',
      sortDescending: false
    },
    { label: 'App Type', sortField: 'workflowName', sortDescending: false },
    { label: 'Registered On', sortField: 'registeredOn', sortDescending: true },
    { label: 'Effective On', sortField: 'effectiveOn', sortDescending: true },
    { label: 'Expires On', sortField: 'expiresOn', sortDescending: true },
    { label: 'Status', sortField: 'status', sortDescending: false }
  ]; 
   isSortOpenAbove = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private workflowSvc: WorkflowService,
    private context: WorkflowContextService,
    private cdr:ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    const storedPageSize = localStorage.getItem(this.PAGE_SIZE_STORAGE_KEY);

    if (storedPageSize) {
      const pageSize = Number(storedPageSize);

      if ([10, 25, 100, 200].includes(pageSize)) {
        this.pageSize = pageSize;
      }
    }
    this.updateViewportState();
    this.columns = this.buildColumns();
    this.displayedColumns = this.columns.map(column => column.key);

    this.subscriptions.add(
      this.route.queryParamMap.subscribe(params => {
        const stateSignature = this.getRouteStateSignature(params);

        if (stateSignature === this.lastRouteStateSignature) {
          return;
        }

        this.pendingQueryParams = params;

        if (this.filtersReady && this.clientId) {
          this.pendingRouteState = this.parseRouteState(params);
          this.applyRouteState(this.pendingRouteState);
          this.fetchRegistrations(this.currentPage, false);
        }
      })
    );

    this.subscriptions.add(
      this.router.events
        .pipe(filter(event => event instanceof NavigationEnd))
        .subscribe(() => {
          const wasOverlayOpen = this.hadOverlayChildRoute;
          this.syncOverlayState();
          if (this.isOverlayOpen) {
            this.isRenewNavigationLoading = false;
            this.renewingRegistrationId = null;
          }
          if (wasOverlayOpen && !this.isOverlayOpen && this.clientId) {
            this.fetchRegistrations(this.currentPage);
          }
          this.hadOverlayChildRoute = this.isOverlayOpen;
        })
    );

    this.subscriptions.add(
      this.route.parent?.paramMap.subscribe(params => {
        const nextClientId =
          params.get('clientId') || this.context.client?.id || '';

        this.clientId = nextClientId;
        if (!this.clientId) {
          this.isLoading = false;
          return;
        }

        this.isLoading = true;
        this.ensureFiltersInitialized().then(() => {
          const routeState = this.pendingQueryParams
            ? this.parseRouteState(this.pendingQueryParams)
            : this.pendingRouteState;

          this.applyRouteState(routeState || this.getCachedRouteState());
          this.syncStateToRoute();
          this.fetchRegistrations(this.currentPage, false);
        });
      }) || new Subscription()
    );

    this.syncOverlayState();
    this.hadOverlayChildRoute = this.isOverlayOpen;
  }
    private routeBodyElement: HTMLElement | null = null;
// Added by Priya: Close the Sort By dropdown when manual scrolling occurs
// within the route body to prevent the dropdown from remaining open
// at an outdated position.
  private closeSortOnScroll = (): void => {
  if (this.isSortOpen) {
    this.isSortOpen = false;
  }
};
  ngAfterViewInit(): void {
          // Added by Priya on 12-10-2026: Get the route-body element to detect manual scrolling.

          this.routeBodyElement = document.querySelector('.route-body');
    // Added by Priya on 12-10-2026: Close the Sort By dropdown when the user manually scrolls
  // the route body without blocking the browser's default scrolling behavior.
  this.routeBodyElement?.addEventListener(
    'scroll',
    this.closeSortOnScroll,
    { passive: true }
  );

    this.syncFilterBuilderState();
  }

  ngOnDestroy(): void {
        this.routeBodyElement?.removeEventListener(
    'scroll',
    this.closeSortOnScroll
  );
    document.body.style.overflow = '';
    this.subscriptions.unsubscribe();
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.isSortOpen = false;
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateViewportState();
    this.syncOverlayState();
  }
// Added by Priya: Dynamically position the Sort By dropdown above or below
// the button based on the actual menu height and available viewport space.
toggleSort(event: MouseEvent): void {
  event.stopPropagation();

  if (this.isSortOpen) {
    this.isSortOpen = false;
    return;
  }

  this.isSortOpen = true;
  this.isSortOpenAbove = false;

  this.cdr.detectChanges();

  requestAnimationFrame(() => {
    const wrapper = this.sortWrapper?.nativeElement as HTMLElement;
    const button = wrapper?.querySelector('.sort-btn') as HTMLElement;
    const menu = wrapper?.querySelector('.sort-menu') as HTMLElement;

    if (!button || !menu) {
      return;
    }

    const buttonRect = button.getBoundingClientRect();
    const menuHeight = menu.getBoundingClientRect().height;

    const spaceBelow = window.innerHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;

    this.isSortOpenAbove =
      spaceBelow < menuHeight && spaceAbove > spaceBelow;

    this.cdr.detectChanges();
  });
}

  selectSort(option: SortOption): void {
    this.sortField = option.sortField;
    this.sortDescending = option.sortDescending;
    this.selectedSortLabel = option.label;
    this.isSortOpen = false;
    this.currentPage = 0;
    this.syncStateToRoute();
    this.fetchRegistrations(0, false);
  }

  onFiltersChanged(filters: FilterBuilderOutput): void {
    const nextSearchQuery = this.getSimpleSearchQuery(filters || {});
    const nextActiveFilters = this.getNonSearchFilters(filters || {});

    if (
      this.isSyncingFilterBuilderState ||
      (nextSearchQuery === this.searchQuery &&
        JSON.stringify(nextActiveFilters) ===
          JSON.stringify(this.activeFilters))
    ) {
      return;
    }

    this.searchQuery = nextSearchQuery;
    this.activeFilters = nextActiveFilters;
    this.defaultFilters = this.buildDefaultFilters(this.activeFilters);
    this.currentPage = 0;
    this.syncStateToRoute();
    this.fetchRegistrations(0, false);
  }

  onSortChanged(event: {
    sortField: string | null;
    sortDescending: boolean | null;
  }): void {
    if (!event.sortField || event.sortDescending === null) {
      return;
    }

    this.sortField = event.sortField;
    this.sortDescending = event.sortDescending;
    this.selectedSortLabel = this.getSortLabel(
      event.sortField,
      event.sortDescending
    );
    this.syncStateToRoute();
    this.fetchRegistrations(this.currentPage, false);
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.syncStateToRoute();
    this.fetchRegistrations(page, false);
  }
  onRowsPerPageChange(size: number) {
    const pageSize = Number(size) || 10;
    localStorage.setItem(this.PAGE_SIZE_STORAGE_KEY, String(pageSize));
    this.pageSize = pageSize;
    this.currentPage = 0;
    this.syncStateToRoute();
    this.fetchRegistrations(this.currentPage, false);
  }

  get isNoData(): boolean {
    return !this.data || this.data.length === 0;
  }

  get hasFewData(): boolean {
    return !!this.data && this.data.length <= 4;
  }

  onRenew(row: RegistrationRow): void {
    if (
      !row?.id ||
      this.renewingRegistrationId ||
      this.isRenewNavigationLoading
    ) {
      return;
    }

    this.renewingRegistrationId = row.id;
    this.isRenewNavigationLoading = true;

    this.subscriptions.add(
      this.workflowSvc
        .startRegistrationRenewal(row.id, false)
        .pipe(
          switchMap(startResult =>
            this.workflowSvc.getCurrentActivity(startResult.applicationId).pipe(
              map(activityResult => ({
                applicationId: startResult.applicationId,
                activityResult
              }))
            )
          ),
          finalize(() => {
            this.renewingRegistrationId = null;
          })
        )
        .subscribe({
          next: ({ applicationId, activityResult }) => {
            const activityDataId = this.getRenewalActivityDataId(
              activityResult
            );

            if (!activityDataId) {
              this.isRenewNavigationLoading = false;
              console.error(
                'No renewal activity found for application',
                applicationId
              );
              return;
            }

            const currentUrl = this.router.url;
            // Added by Amisha on 06/15/2026.
            // Reason: keeps the renewals return URL readable in the address bar while preserving
            // the same back-navigation params needed to return the user to My Renewals.
            const targetUrl =
              `${this.router.url.split('?')[0]}/workflow-application/` +
              `${applicationId}/${activityDataId}` +
              `?returnToRegistrations=1&returnURL=${currentUrl}`;

            this.router.navigateByUrl(targetUrl).catch(() => {
              this.isRenewNavigationLoading = false;
            });
          },
          error: error => {
            this.isRenewNavigationLoading = false;
            console.error('Unable to start renewal flow', error);
          }
        })
    );
  }

  private ensureFiltersInitialized(): Promise<void> {
    if (
      this.filtersReady &&
      this.initializedFiltersClientId === this.clientId
    ) {
      return Promise.resolve();
    }

    if (this.initFiltersPromise) {
      return this.initFiltersPromise;
    }

    this.initFiltersPromise = this.initFilters()
      .then(() => {
        this.initializedFiltersClientId = this.clientId;
        this.filtersReady = true;
        this.syncFilterBuilderState();
      })
      .finally(() => {
        this.initFiltersPromise = null;
      });

    return this.initFiltersPromise;
  }

  private initFilters(): Promise<void> {
    // Added by Amisha on 06/17/2026.
    // Reason: the renewals Type filter should come from renewable workflows configured for the
    // current jurisdiction and visible on the applicant dashboard, not from the user's existing rows.
    const jurisdictionClient = this.clientId
      ? new Client({ id: this.clientId })
      : this.context.client;
    const renewableWorkflows$ = jurisdictionClient
      ? this.workflowSvc.getRenewableWorkflows(jurisdictionClient, false, false)
      : of({ registrations: [] });

    return renewableWorkflows$.toPromise().then(renewableWorkflows => {
      this.workflowOpts = (renewableWorkflows || [])
        .filter((workflow: any) => workflow?.showInApplications !== false)
        .map(workflow => ({
          name: workflow.version?.name ?? workflow.name,
          value: workflow.version?.workflowId ?? workflow.id
        }))
        .sort((left, right) => left.name.localeCompare(right.name));

      this.filterCriteria = [
        { name: 'Registration Info', id: 'appDescription' },
        {
          name: 'App Type',
          id: 'renewalType',
          types: ['is', 'in'],
          options: [...this.workflowOpts]
        },
        {
          name: 'Registered On',
          id: 'registeredOn',
          inputType: 'date',
          types: ['is', 'range']
        },
        {
          name: 'Effective On',
          id: 'effectiveOn',
          inputType: 'date',
          types: ['is', 'range']
        },
        {
          name: 'Expires On',
          id: 'expiresOn',
          inputType: 'date',
          types: ['is', 'range']
        },
        {
          name: 'Status',
          id: 'status',
          types: ['is', 'contains', 'in'],
          options: [
            { name: 'Expired', value: 'Expired' },
            { name: 'Expiring Soon', value: 'Expiring Soon' },
            { name: 'Inactive', value: 'Inactive' }
          ]
        }
      ];
    });
  }

  private buildColumns(): TableColumn[] {
    return [
      {
        key: 'registrationInfoText',
        label: 'Registration Info',
        headerType: 'text',
        cellType: 'text',
        width: '177px'
      },
      {
        key: 'type',
        label: 'App Type',
        headerType: 'text',
        cellType: 'text',
        width: '100px'
      },
      {
        key: 'registeredOn',
        label: 'Registered On',
        headerType: 'text',
        cellType: 'text',
        width: '110px'
      },
      {
        key: 'effectiveOn',
        label: 'Effective On',
        headerType: 'text',
        cellType: 'text',
        width: '110px'
      },
      {
        key: 'expiresOn',
        label: 'Expires On',
        headerType: 'text',
        cellType: 'text',
        width: '95px'
      },
      {
        key: 'status',
        label: 'Status',
        headerType: 'text',
        cellType: 'component',
        component: MyRegistrationsStatusPillComponent,
        componentInputs: (row: RegistrationRow) => ({ row }),
        width: '90px',
        sortable: false
      },
      {
        key: 'renewLabel',
        label: 'Renew',
        headerType: 'text',
        cellType: 'component',
        component: MyRegistrationsActionCellComponent,
        componentInputs: (row: RegistrationRow) => ({
          row,
          onRenew: () => this.onRenew(row)
        }),
        width: '72px',
        sortable: false
      }
    ];
  }

  private fetchRegistrations(page: number, syncRoute = true): void {
    if (!this.clientId) {
      return;
    }

    this.currentPage = page;
    if (syncRoute) {
      this.syncStateToRoute();
    }
    this.isLoading = true;

    const filters = this.buildApiFilters();
    const requestKey = JSON.stringify({
      clientId: this.clientId,
      sortField: this.sortField,
      sortDescending: this.sortDescending,
      page: this.currentPage,
      pageSize: this.pageSize,
      filters
    });

    // Added by Amisha on 06/17/2026.
    // Reason: route normalization plus filter-builder state sync can fire the same renewals search
    // repeatedly, so skip exact duplicate in-flight requests and let the first response win.
    if (this.activeFetchRequestKey === requestKey) {
      return;
    }

    this.activeFetchRequestKey = requestKey;

    this.subscriptions.add(
      this.workflowSvc
        .searchRegistrationsPaged(
          this.clientId,
          this.sortField,
          this.sortDescending,
          filters,
          this.currentPage,
          this.pageSize
        )
        .subscribe({
          next: result => {
            this.totalRecords = result.recordCount ?? 0;
            this.totalPages = Math.max(1, result.pageCount ?? 1);

            this.data = (result.registrations ?? [])
              .map((registration): RegistrationRow | null => {
                const registrationName = this.normalizeRegistrationInfoText(
                  registration.appDescription
                );
                const mappedStatus = this.getDisplayStatus(
                  registration.status,
                  registration.expiresOnString
                );

                if (!mappedStatus) {
                  return null;
                }

                return {
                  id: registration.id,
                  applicationId:
                    registration.registerApplicationId ?? undefined,
                  registrationInfo: {
                    name: registrationName
                  },
                  registrationInfoText: registrationName,
                  type: registration.workflowName ?? '',
                  registeredOn: registration.registeredOnString ?? '',
                  effectiveOn: registration.effectiveOnString ?? '',
                  expiresOn: registration.expiresOnString ?? '',
                  status: mappedStatus,
                  renewLabel: 'Renew'
                };
              })
              .filter(
                (registration): registration is RegistrationRow =>
                  !!registration
              );

            this.data = this.applyLocalStatusFilter(this.data);

            this.isLoading = false;
            this.activeFetchRequestKey = null;
          },
          error: () => {
            this.data = [];
            this.totalRecords = 0;
            this.totalPages = 1;
            this.isLoading = false;
            this.activeFetchRequestKey = null;
          }
        })
    );
  }

  private buildApiFilters(): ItemSearchOptionField[] {
    const legacyFilters = this.buildLegacyApiFilters(
      this.activeFilters || {}
    ).filter(filter => filter.title !== 'status');
    const simpleSearchFilters = this.searchQuery
      ? this.buildLegacyApiFilters(
          this.buildSimpleSearchFilters(this.searchQuery)
        )
      : [];
    const applicantFilter = this.buildApplicantFilter();
    const expiringWithinPeriodFilter = this.buildExpiringWithinPeriodFilter();
    const statusFilter = this.buidStatusFilter();

    return this.mergeApiFilters([
      ...legacyFilters,
      ...simpleSearchFilters,
      ...(applicantFilter ? [applicantFilter] : []),
      expiringWithinPeriodFilter,
      statusFilter
    ]);
  }

  private buidStatusFilter(): ItemSearchOptionField {
    return {
      title: 'status',
      options: [
        new ItemSearchOptionFieldOption({
          title: 'status',
          selected: true,
          filterText: 'Expired,Inactive,Approved',
          searchText: 'Expired,Inactive,Approved',
          strOperator: 'in'
        })
      ]
    };
  }

  private buildApplicantFilter(): ItemSearchOptionField | null {
    const userId = this.context.user?.id;

    if (!userId) {
      return null;
    }

    return {
      title: 'userId',
      options: [
        new ItemSearchOptionFieldOption({
          title: 'userId',
          selected: true,
          filterText: '',
          searchText: userId,
          strOperator: 'is'
        })
      ]
    };
  }

  private buildExpiringWithinPeriodFilter(): ItemSearchOptionField {
    return {
      title: '<expiresOn',
      options: [
        new ItemSearchOptionFieldOption({
          title: '<expiresOn',
          selected: true,
          filterText: this.getThirtyDaysFromNowDateString(),
          searchText: this.getThirtyDaysFromNowDateString(),
          strOperator: '<'
        })
      ]
    };
  }

  private getDisplayStatus(
    status: number,
    expiresOn: string | null | undefined
  ): RegistrationStatus | null {
    const expiryDate = this.parseDateString(expiresOn);

    if (!expiryDate) {
      return null;
    }

    const today = this.getStartOfDay(new Date());
    const thresholdDate = this.getStartOfDay(new Date());
    thresholdDate.setDate(thresholdDate.getDate() + 30);

    // RegistrationStatus.Inactive = 4
    if (status === 4) {
      return 'Inactive';
    }

    if (status === 3 || expiryDate < today) {
      return 'Expired';
    }

    if (expiryDate >= today && expiryDate < thresholdDate) {
      return 'Expiring Soon';
    }

    return null;
  }

  private getSortLabel(sortField: string, sortDescending: boolean): string {
    const matchingOption = this.sortOptions.find(
      option =>
        option.sortField === sortField &&
        option.sortDescending === sortDescending
    );

    return matchingOption?.label || 'Custom';
  }

  private normalizeRegistrationInfoText(
    value: string | null | undefined
  ): string {
    return (value ?? '').replace(/\s+/g, ' ').trim();
  }

  private getThirtyDaysFromNowDateString(): string {
    const today = new Date();
    today.setDate(today.getDate() + 30);

    return `${today.getMonth() + 1}/${today.getDate()}/${today.getFullYear()}`;
  }

  private applyLocalStatusFilter(rows: RegistrationRow[]): RegistrationRow[] {
    const statusFilter = this.activeFilters?.status;
    const filterValue = (statusFilter?.value || '').trim().toLowerCase();

    if (!filterValue) {
      return rows;
    }

    if ((statusFilter?.type || 'is') === 'contains') {
      return rows.filter(row => row.status.toLowerCase().includes(filterValue));
    }

    return rows.filter(row => row.status.toLowerCase() === filterValue);
  }

  private parseDateString(value: string | null | undefined): Date | null {
    if (!value) {
      return null;
    }

    const parts = value.split('/').map(part => Number(part));

    if (parts.length !== 3 || parts.some(part => Number.isNaN(part))) {
      return null;
    }

    const [month, day, year] = parts;
    return this.getStartOfDay(new Date(year, month - 1, day));
  }

  private getStartOfDay(date: Date): Date {
    const normalizedDate = new Date(date);
    normalizedDate.setHours(0, 0, 0, 0);
    return normalizedDate;
  }

  private getRenewalActivityDataId(activityResult: any): string | null {
    if (activityResult?.activity?.activityDataId) {
      return activityResult.activity.activityDataId;
    }

    const formActivity = activityResult?.navigationItems?.find(
      (activity: any) => activity?.activityType === 'form-activity'
    );

    return formActivity?.activityDataId ?? null;
  }

  private updateViewportState(): void {
    this.isMobileDevice = window.innerWidth < 768;
  }

  private syncOverlayState(): void {
    const hasChildRoute = !!this.route.firstChild;

    this.isOverlayOpen = hasChildRoute;
    this.showOverlay = false;

    document.body.style.overflow =
      hasChildRoute || this.isRenewNavigationLoading ? 'hidden' : '';
  }

  private applyRouteState(state: MyRegistrationsRouteState | null): void {
    const normalizedState = state || this.getDefaultRouteState();

    this.currentPage = normalizedState.page;
    this.sortField = normalizedState.sortField;
    this.sortDescending = normalizedState.sortDescending;
    this.selectedSortLabel = this.getSortLabel(
      this.sortField,
      this.sortDescending
    );
    this.searchQuery = normalizedState.searchQuery || '';
    this.activeFilters = this.getNonSearchFilters(
      normalizedState.activeFilters || {}
    );
    this.defaultFilters = this.buildDefaultFilters(this.activeFilters);
    this.syncFilterBuilderState();
  }

  private syncStateToRoute(): void {
    const routeState = this.normalizeRouteState({
      page: this.currentPage,
      searchQuery: this.searchQuery,
      activeFilters: this.activeFilters,
      sortField: this.sortField,
      sortDescending: this.sortDescending
    });

    this.persistRouteState(routeState);

    const queryParams = {
      [this.PAGE_QUERY_PARAM]: routeState.page + 1,
      [this.SEARCH_QUERY_PARAM]: routeState.searchQuery || null,
      [this.FILTER_QUERY_PARAM]: this.serializeFilterTokens(
        this.getNonSearchFilters(routeState.activeFilters)
      ),
      [this.SORT_FIELD_QUERY_PARAM]:
        routeState.sortField !== this.sortOptions[0].sortField
          ? routeState.sortField
          : null,
      [this.SORT_DESC_QUERY_PARAM]:
        routeState.sortDescending !== this.sortOptions[0].sortDescending
          ? routeState.sortDescending
            ? '1'
            : '0'
          : null
    };

    this.lastRouteStateSignature = this.getRouteStateSignatureFromQueryParams(
      queryParams
    );

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  private parseRouteState(params: ParamMap): MyRegistrationsRouteState {
    const pageFromRoute = Number(params.get(this.PAGE_QUERY_PARAM) || '1');
    const page =
      Number.isFinite(pageFromRoute) && pageFromRoute > 0
        ? pageFromRoute - 1
        : 0;
    const searchQuery = params.get(this.SEARCH_QUERY_PARAM) || '';
    const activeFilters = this.buildRouteFilters(
      searchQuery,
      params.getAll(this.FILTER_QUERY_PARAM)
    );
    const defaultSort = this.sortOptions[0];

    return this.normalizeRouteState({
      page,
      searchQuery,
      activeFilters,
      sortField:
        params.get(this.SORT_FIELD_QUERY_PARAM) || defaultSort.sortField,
      sortDescending:
        params.get(this.SORT_DESC_QUERY_PARAM) == null
          ? defaultSort.sortDescending
          : params.get(this.SORT_DESC_QUERY_PARAM) !== '0'
    });
  }

  private getDefaultRouteState(): MyRegistrationsRouteState {
    const defaultSort = this.sortOptions[0];

    return {
      page: 0,
      searchQuery: '',
      activeFilters: {},
      sortField: defaultSort.sortField,
      sortDescending: defaultSort.sortDescending
    };
  }

  private normalizeRouteState(
    state: Partial<MyRegistrationsRouteState>
  ): MyRegistrationsRouteState {
    const defaultState = this.getDefaultRouteState();
    const searchQuery =
      state.searchQuery || this.getSimpleSearchQuery(state.activeFilters);
    const activeFilters = this.getNonSearchFilters(state.activeFilters || {});

    return {
      page: state.page ?? defaultState.page,
      searchQuery,
      activeFilters,
      sortField: state.sortField || defaultState.sortField,
      sortDescending:
        typeof state.sortDescending === 'boolean'
          ? state.sortDescending
          : defaultState.sortDescending
    };
  }

  private buildRouteFilters(
    searchQuery: string,
    tokens: string[]
  ): FilterBuilderOutput {
    const filters: FilterBuilderOutput = {};

    tokens.forEach(token => {
      const [rawId = '', rawType = '', ...rawValueParts] = token.split(':');
      const id = rawId || '';
      const type = rawType || '';
      const param = this.filterCriteria.find(filter => filter.id === id);
      const value = this.denormalizeRouteFilterValue(
        param,
        rawValueParts.join(':') || ''
      );

      if (!id || !param) {
        return;
      }

      filters[id] = {
        id,
        type: type || (param.types?.[0] ?? 'contains'),
        value,
        inputType: param.inputType
      };
    });

    return filters;
  }

  private buildSimpleSearchFilters(searchQuery: string): FilterBuilderOutput {
    const value = (searchQuery || '').trim();

    if (!value) {
      return {};
    }

    // Keep applicant simple search aligned with the working staff renewals payload.
    return this.simpleSearchCriteria.reduce((acc, criterion) => {
      acc[criterion.id] = {
        id: criterion.id,
        type: 'contains',
        value,
        inputType: criterion.inputType,
        searchOptionMethod: 'simpleSearch'
      };

      return acc;
    }, {} as FilterBuilderOutput);
  }

  private buildLegacyApiFilters(
    filters: FilterBuilderOutput = {}
  ): ItemSearchOptionField[] {
    return Object.values(filters || {}).reduce((result, filter) => {
      if (!filter?.id) {
        return result;
      }

      const rawValue = filter.value == null ? '' : String(filter.value).trim();
      const isSimpleSearch = filter.searchOptionMethod === 'simpleSearch';

      if (
        !rawValue &&
        filter.type !== 'exists' &&
        filter.type !== 'not exists'
      ) {
        return result;
      }

      result.push(
        new ItemSearchOptionField({
          title: filter.id,
          options: [
            new ItemSearchOptionFieldOption({
              title: filter.id,
              selected: true,
              filterText:
                !isSimpleSearch && filter.type === 'contains' ? rawValue : '',
              searchText:
                isSimpleSearch || filter.type !== 'contains' ? rawValue : '',
              strOperator: filter.type || '',
              searchOptionMethod: filter.searchOptionMethod,
              inputType: filter.inputType
            })
          ]
        })
      );

      return result;
    }, [] as ItemSearchOptionField[]);
  }

  private mergeApiFilters(
    filters: ItemSearchOptionField[] = []
  ): ItemSearchOptionField[] {
    const grouped = new Map<string, ItemSearchOptionField>();

    for (const filter of filters || []) {
      if (!filter?.title) {
        continue;
      }

      const normalizedOptions = (filter.options || []).filter(option => {
        const filterText = String(option?.filterText || '').trim();
        const searchText = String(option?.searchText || '').trim();

        return !!filterText || !!searchText;
      });

      if (!normalizedOptions.length) {
        continue;
      }

      const existing = grouped.get(filter.title);

      if (existing) {
        existing.options.push(...normalizedOptions);
        continue;
      }

      grouped.set(
        filter.title,
        new ItemSearchOptionField({
          title: filter.title,
          options: normalizedOptions
        })
      );
    }

    return Array.from(grouped.values());
  }

  private getSimpleSearchQuery(filters: FilterBuilderOutput = {}): string {
    const simpleSearchFilter = Object.values(filters || {}).find(
      filter => filter?.searchOptionMethod === 'simpleSearch'
    );

    return (simpleSearchFilter?.value || '').trim();
  }

  private getNonSearchFilters(
    filters: FilterBuilderOutput = {}
  ): FilterBuilderOutput {
    return Object.values(filters || {}).reduce((acc, filter) => {
      if (!filter || filter.searchOptionMethod === 'simpleSearch') {
        return acc;
      }

      acc[filter.id] = { ...filter };
      return acc;
    }, {} as FilterBuilderOutput);
  }

  private buildDefaultFilters(
    filters: FilterBuilderOutput = {}
  ): FilterBuilderFilter[] {
    return Object.values(filters || {})
      .map(filter => {
        const param = this.filterCriteria.find(item => item.id === filter.id);

        if (!param) {
          return null;
        }

        return new FilterBuilderFilter({
          param,
          paramId: param.id,
          queryType: filter.type,
          value: filter.value,
          inputType: filter.inputType,
          searchOptionMethod: filter.searchOptionMethod
        });
      })
      .filter((filter): filter is FilterBuilderFilter => !!filter);
  }

  private serializeFilterTokens(filters: FilterBuilderOutput): string[] | null {
    const tokens = Object.values(filters || {})
      .map(filter =>
        [
          filter.id || '',
          filter.type || '',
          this.normalizeRouteFilterValue(filter.id, filter.value || '')
        ].join(':')
      )
      .filter(token => token !== '::');

    return tokens.length > 0 ? tokens : null;
  }

  private normalizeRouteFilterValue(filterId: string, value: string): string {
    const param = this.filterCriteria.find(filter => filter.id === filterId);

    if (!param?.options?.length) {
      return value;
    }

    const optionValue = this.findParamOptionValue(param, value);
    return optionValue ? optionValue.replace(/\s+/g, '') : value;
  }

  private denormalizeRouteFilterValue(
    param: FilterBuilderParam | undefined,
    value: string
  ): string {
    if (!param?.options?.length) {
      return value;
    }

    return this.findParamOptionValue(param, value) || value;
  }

  private findParamOptionValue(
    param: FilterBuilderParam,
    value: string
  ): string | null {
    const normalizedValue = (value || '').replace(/\s+/g, '').toLowerCase();
    const matchingOption = (param.options || []).find(option => {
      if (typeof option === 'string') {
        return option.replace(/\s+/g, '').toLowerCase() === normalizedValue;
      }

      return option.value.replace(/\s+/g, '').toLowerCase() === normalizedValue;
    });

    if (!matchingOption) {
      return null;
    }

    return typeof matchingOption === 'string'
      ? matchingOption
      : matchingOption.value;
  }

  private persistRouteState(state: MyRegistrationsRouteState): void {
    localStorage.setItem(this.FILTER_CACHE_KEY, JSON.stringify(state));
  }

  private getCachedRouteState(): MyRegistrationsRouteState | null {
    try {
      const rawState = localStorage.getItem(this.FILTER_CACHE_KEY);

      return rawState ? this.normalizeRouteState(JSON.parse(rawState)) : null;
    } catch {
      return null;
    }
  }

  private syncFilterBuilderState(): void {
    if (!this.filterBuilderComponent) {
      return;
    }

    const nextFilters = this.buildDefaultFilters(this.activeFilters);
    const nextValue = this.searchQuery || '';

    setTimeout(() => {
      if (this.filterBuilderComponent) {
        this.isSyncingFilterBuilderState = true;
        this.filterBuilderComponent.filters = nextFilters.map(
          filter =>
            new FilterBuilderFilter({
              param: filter.param,
              paramId: filter.paramId,
              queryType: filter.queryType,
              value: filter.value,
              inputType: filter.inputType,
              searchOptionMethod: filter.searchOptionMethod
            })
        );
        this.filterBuilderComponent.simpleFilters = [];
        this.filterBuilderComponent.simpleSearchModel = nextValue;
        this.filterBuilderComponent.changeSimpleSearch();
        setTimeout(() => {
          this.isSyncingFilterBuilderState = false;
        });
      }
    });
  }

  private getRouteStateSignature(params: ParamMap): string {
    return JSON.stringify({
      p: params.get(this.PAGE_QUERY_PARAM) ?? null,
      q: params.get(this.SEARCH_QUERY_PARAM) ?? null,
      af: params.getAll(this.FILTER_QUERY_PARAM),
      sf: params.get(this.SORT_FIELD_QUERY_PARAM) ?? null,
      sd: params.get(this.SORT_DESC_QUERY_PARAM) ?? null
    });
  }

  private getRouteStateSignatureFromQueryParams(queryParams: {
    [key: string]: string | number | string[] | null;
  }): string {
    return JSON.stringify({
      p: queryParams[this.PAGE_QUERY_PARAM] ?? null,
      q: queryParams[this.SEARCH_QUERY_PARAM] ?? null,
      af: queryParams[this.FILTER_QUERY_PARAM] ?? [],
      sf: queryParams[this.SORT_FIELD_QUERY_PARAM] ?? null,
      sd: queryParams[this.SORT_DESC_QUERY_PARAM] ?? null
    });
  }
}
